import { CHANNEL_NAMES } from '@/config/constants';
import Schedule from '@/models/Schedule';
import { EpgModel } from '@/models/EpgTypes';
import moment from 'moment';
import { CartItem } from '@/models/ProductTypes';

export function formatLink(link: string): string {
  // Check if it's a full URL using a regex (starting with http, https, or www, mailto: or tel:)
  if (/^(https?:\/\/|mailto:|tel:)/.test(link)) {
    return link;
  }
  if (/^www\./.test(link)) {
    return 'https://' + link;
  }

  // Ensure the path starts with a '/'
  return link.startsWith('/') ? link : '/' + link;
}

export function cleanLink(link: string): string {
  let formattedLink = link.replace(/(^\w+:|^)\/\//, ''); // Removing 'http://' or 'https://'
  formattedLink = formattedLink.replace('www.', ''); // Removing 'www.'
  return formattedLink;
}

export const isValidKennitalaInput = (kennitala: string): boolean => {
  const tenDigitsOrLessRegex = /^\d{0,10}$/;
  return tenDigitsOrLessRegex.test(kennitala);
};

export const isValidKennitala = (kennitala: string): boolean => {
  const tenDigitsRegex = /^\d{10}$/;
  return tenDigitsRegex.test(kennitala);
};

export const isValidPhoneNumberInput = (phoneNumber: string): boolean => {
  const hyphenatedPhoneRegex = /^(\d{0,3}|\d{3}-\d{0,4})$/;
  return hyphenatedPhoneRegex.test(phoneNumber);
};

export const isNumber = (number: string) => {
  const numberRegex = /^$|^[0-9]+$/;
  return numberRegex.test(number);
};

/**
 * A helper function that returns a fully formed url from Appsync search engine
 * @param url The url returned from the Appsync search engine
 * @param type The type of search returned from the Appsync search engine
 * @returns A search url
 */
export const formatSearchLink = (url: string, type: string) => {
  switch (type) {
    case 'product': {
      const vefverslunBase = process.env.NEXT_PUBLIC_VEFVERSLUN_URL;
      return `${vefverslunBase}/vorur/nanar-um-voru/?productid=${url}`;
    }
    case 'NewsPage': {
      return `/frettir/${url}`;
    }
    default:
      return url;
  }
};

export const formatLabelText = (type: string) => {
  switch (type) {
    case 'product': {
      return `Vara`;
    }
    case 'NewsPage': {
      return `Frétt`;
    }
    case 'Page':
    case 'page': {
      return `Síða`;
    }
    default:
      return type;
  }
};

// TODO: Implement these placeholder icons
export enum ICON_TYPE {
  ADD = 'ADD',
  ARROW_DOWN = 'ARROW_DOWN',
  ARROW_LEFT = 'ARROW_LEFT',
  ARROW_RIGHT = 'ARROW_RIGHT',
  ARROW_RIGHT_L = 'ARROW_RIGHT_L',
  BIO = 'BIO',
  CHILDREN = 'CHILDREN',
  CLOSE = 'CLOSE',
  EDIT = 'EDIT',
  CATCHUP = 'CATCHUP',
  GRID = 'GRID',
  HOME = 'HOME',
  HELP = 'HELP',
  HIDE = 'HIDE',
  INFO = 'INFO',
  SPORTS = 'SPORTS',
  SEARCH_SM = 'SEARCH_SM',
  LIST = 'LIST',
  MY_LIST = 'MY_LIST',
  MENU = 'MENU',
  PLAY = 'PLAY',
  REMOVE = 'REMOVE',
  REWIND = 'REWIND',
  SEARCH_LG = 'SEARCH_LG',
  SHOW = 'SHOW',
  TV = 'TV',
  SOUND_ON = 'SOUND_ON',
  SOUND_OFF = 'SOUND_OFF',
  SETTINGS_LG = 'SETTINGS_LG',
  EPISODES = 'EPISODES',
  USER = 'USER',
  EDIT_PROFILE = 'EDIT_PROFILE',
  SETTINGS_SM = 'SETTINGS_SM',
  EXIT = 'EXIT',
  SET_TOP_BOX = 'SET_TOP_BOX',
  APPLE_TV = 'APPLE_TV',
  COMPUTER = 'COMPUTER',
  SMARTDEVICES = 'SMARTDEVICES',
  SYN = 'SYN',
  PHONE_RING = 'PHONE_RING',
  FACEBOOK_COLOR = 'FACEBOOK_COLOR',
  YOUTUBE_COLOR = 'YOUTUBE_COLOR',
  INSTAGRAM_COLOR = 'INSTAGRAM_COLOR',
  STOD2_LOGO = 'STOD2_LOGO',
  STOD2_PLUS_LOGO = 'STOD2_PLUS_LOGO',
  OLIS_DEILD = 'OLIS_DEILD',
  PRESENT = 'PRESENT',
  STOD3 = 'STOD3',
  STOD2BIO = 'STOD2BIO',
  SPORT = 'SPORT',
  SPORT2 = 'SPORT2',
  SPORT3 = 'SPORT3',
  SPORT4 = 'SPORT4',
  GOLFSTODIN = 'GOLFSTODIN',
  ESPORT = 'ESPORT',
  WARNING = 'WARNING',
  VODASPORT = 'VODASPORT',
}

export const getYesterday = () => {
  const oldDate = new Date();
  oldDate.setDate(oldDate.getDate() - 1);
  return oldDate.toISOString().slice(0, 10);
};

export const getNextWeek = () => {
  const dates = [];
  const currDate = new Date();
  for (let i = 0; i < 8; i++) {
    dates.push(new Date(currDate).toISOString().slice(0, 10));
    currDate.setDate(currDate.getDate() + 1);
  }
  return dates;
};

export const filterScheduleByDate = (schedule: Schedule[], date?: string, division?: string) => {
  const filteredSchedule = [];
  const lastDate = getNextWeek().pop();
  const dateText = date?.slice(0, 10) ?? '';

  for (let i = 0; i < schedule?.length; i++) {
    if (schedule[i]?.dagsetning?.includes(dateText)) {
      if (division && !(schedule[i]?.titill === division)) {
        // eslint-disable-next-line no-continue
        continue;
      }
      filteredSchedule.push(schedule[i]);
    }
    if (lastDate && !dateText && schedule[i + 1]?.dagsetning?.slice(0, 10) > lastDate) {
      break;
    }
  }

  return filteredSchedule;
};

export const getSchedule = async (channel: string) => {
  const result = await fetch(`/api/epg/${channel}`, {
    next: { revalidate: 43200 },
  });
  return result.json();
};

export const getOldSchedule = async (channel: string, date: string) => {
  const result = await fetch(`/api/epg/${channel}/${date}`, {
    next: { revalidate: 43200 },
  });
  return result.json();
};

export const getChannels = async (type?: string) => {
  const result = await fetch(`/api/epg?type=${type}`, {
    next: { revalidate: 43200 },
  });
  const res = await result.json();

  // Filter out channels that we dont want, we need to change the bucket on aws later so that we don't need to do this
  const popList = ['beint', 'bio', 'esport', 'stod3'];
  const filtered = res.filter((item: string) => !popList.includes(item));

  return filtered;
};

export const getChannelName = (channelId: string | undefined) => {
  if (!channelId) {
    return CHANNEL_NAMES['default'];
  } else {
    return CHANNEL_NAMES[channelId.toLocaleLowerCase() as keyof typeof CHANNEL_NAMES] ?? channelId;
  }
};

const ensureBundesligaOrder = (divisionNames: string[]) => {
  const bundesligaIndex = divisionNames.indexOf('Bundesliga');
  const secondBundesligaIndex = divisionNames.indexOf('2. Bundesliga');

  if (bundesligaIndex === -1 || secondBundesligaIndex === -1) {
    return divisionNames;
  }

  if (secondBundesligaIndex === bundesligaIndex + 1) {
    return divisionNames;
  }

  const adjustedNames = [...divisionNames];
  const [secondBundesliga] = adjustedNames.splice(secondBundesligaIndex, 1);
  const updatedBundesligaIndex = adjustedNames.indexOf('Bundesliga');
  adjustedNames.splice(updatedBundesligaIndex + 1, 0, secondBundesliga);

  return adjustedNames;
};

export const getDivisions = async () => {
  const divisions = new Set<string>();
  const divisionsResult = await fetch('/api/epg/beint', {
    next: { revalidate: 43200 },
  });
  const divisionsJson = await divisionsResult.json();

  for (let i = 0; i < divisionsJson?.length; i++) {
    const divisionTitle = divisionsJson[i]?.titill;
    if (typeof divisionTitle === 'string') {
      divisions.add(divisionTitle);
    }
  }
  const sortedDivisions = Array.from(divisions).sort((a, b) => a.localeCompare(b, undefined, { numeric: false }));
  return ensureBundesligaOrder(sortedDivisions);
};

export const capitalizeFirst = (s: string) => {
  return `${s.substring(0, 1).toUpperCase()}${s.substring(1)}`;
};

export const groupEpgByDate = (epgs: EpgModel[]): { [key: string]: EpgModel[] } => {
  const dict: { [key: string]: EpgModel[] } = {};
  for (const item of epgs) {
    const key = item.dagsetning;
    if (!dict.hasOwnProperty(key)) {
      dict[key] = [];
    }
    dict[key].push(item);
  }
  return dict;
};

export const epgsToText = (epgs: EpgModel[]): string => {
  const epgByDate = groupEpgByDate(epgs);
  let stringBuilder = '';
  Object.keys(epgByDate).forEach((dateKey) => {
    const date = moment(dateKey);
    date.locale('is');
    stringBuilder +=
      capitalizeFirst(date.format('dddd')) +
      date.format(' DD. MMMM yyyy') +
      '\n' +
      epgByDate[dateKey][0].midill_heiti.toUpperCase() +
      '\n\n';
    epgByDate[dateKey].forEach((epg) => {
      const start = moment(epg.upphaf);
      start.locale('is');
      stringBuilder += `${start.format('HH:mm')} ${epg.isltitill}${
        epg.thattafjoldi === 0 ? '\n' : ` (${epg.thattur}:${epg.thattafjoldi})\n`
      }`;
      stringBuilder += epg.titill ? `      (${epg.titill})\n` : '';
      stringBuilder += epg.lysing ? `      ${epg.lysing}\n` : '';
    });
    stringBuilder += '\n';
  });
  return stringBuilder;
};

export const bezierEase = [0.8, 0, 0.1, 1];

export const downloadBlob = (blob: Blob, name?: string | undefined | null) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name || 'file';
  document.body.appendChild(a);
  a.click();
  // Remove element post usage
  a.remove();
};

/* ------------------------- tiny runtime-safe helpers ------------------------ */
export const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export const getBool = (o: unknown, key: string): boolean | undefined => {
  if (!isObj(o)) return undefined;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const v = (o as any)[key];
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    if (v.toLowerCase() === 'true') return true;
    if (v.toLowerCase() === 'false') return false;
  }
  return undefined;
};

/** deterministic ISK formatter (SSR/client identical) */
export const formatISK = (n?: number): string =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${Math.round(n)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} kr.`
    : '—';

export const normHex = (hex?: string) => (hex || '').toLowerCase().replace('#', '');
export const colorToCss = (hex?: string) => (/^[0-9a-f]{6}$/i.test(normHex(hex)) ? `#${normHex(hex)}` : undefined);

export const toThousands = (number: number): string => {
  return `${Math.floor(number / 1000)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}Þ`;
};

export const hashCart = async (cart: Record<string, CartItem>) => {
  const keys = Object.keys(cart);
  const itemString = keys.reduce(
    (aggregate: string, key) =>
      aggregate.length > 0
        ? `${aggregate}:${cart[key].quantity}x${cart[key].product.id}`
        : `${cart[key].quantity}x${cart[key].product.id}`,
    ''
  );
  const total = keys.reduce((sum, key) => sum + cart[key].quantity * cart[key].product.price, 0);
  const result = await fetch('/api/store/hash', {
    method: 'POST',
    body: JSON.stringify({ buffer: `${itemString}:${total}` }),
  });
  const resultJson = await result.json();
  return resultJson.hash;
};
